import { useState, useCallback } from 'react';
import { getApiErrorMessage } from '../../../../lib/utils';
import {
  getSkillFileContent,
  useUploadSkillFile,
  useDeleteSkillFile,
  type AgentSkillScope,
} from '../../../../api/agent-skills';
export interface FilePreviewState {
  path: string;
  content: string;
  isLoading: boolean;
  error: string | null;
}
export interface UploadProgressEntry {
  loaded: number;
  total: number;
  percentage: number;
}
export function useSkillFiles(
  skill: { scope: AgentSkillScope; name: string } | null,
) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<
    Map<string, UploadProgressEntry>
  >(new Map());
  const [previewFile, setPreviewFile] = useState<FilePreviewState | null>(null);
  const [editingContent, setEditingContent] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showAddFile, setShowAddFile] = useState(false);
  const [confirmDeleteFile, setConfirmDeleteFile] = useState<string | null>(
    null,
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteFileError, setDeleteFileError] = useState<string | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const uploadFile = useUploadSkillFile(skill!);
  const deleteFile = useDeleteSkillFile(skill!);
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);
  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);
  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDropError(null);
      if (!skill) {
        return;
      }
      const files = Array.from(e.dataTransfer.files);
      for (const file of files) {
        // For text files < 50KB, use direct upload
        if (file.size < 50 * 1024) {
          try {
            const text = await file.text();
            setUploadProgress((prev) => {
              const next = new Map(prev);
              next.set(file.name, {
                loaded: 0,
                total: file.size,
                percentage: 0,
              });
              return next;
            });
            await uploadFile.mutateAsync({
              path: file.name,
              content: text,
            });
            setUploadProgress((prev) => {
              const next = new Map(prev);
              next.set(file.name, {
                loaded: file.size,
                total: file.size,
                percentage: 100,
              });
              return next;
            });
            // Clear progress after a delay
            setTimeout(() => {
              setUploadProgress((prev) => {
                const next = new Map(prev);
                next.delete(file.name);
                return next;
              });
            }, 2000);
          } catch (err) {
            setUploadProgress((prev) => {
              const next = new Map(prev);
              next.delete(file.name);
              return next;
            });
            setDropError(getApiErrorMessage(err, `Failed to upload ${file.name}`));
          }
        }
      }
    },
    [skill, uploadFile],
  );
  const loadFileContent = useCallback(
    async (filePath: string) => {
      if (!skill) {
        return;
      }
      setPreviewFile({
        path: filePath,
        content: '',
        isLoading: true,
        error: null,
      });
      setIsEditing(false);
      setEditingContent(null);
      setSaveError(null);
      try {
        const res = await getSkillFileContent(skill, filePath);
        setPreviewFile({
          path: filePath,
          content: res.content,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setPreviewFile({
          path: filePath,
          content: '',
          isLoading: false,
          error: getApiErrorMessage(err, 'Failed to load file'),
        });
      }
    },
    [skill?.name ?? 'none'],
  );
  const startEditing = useCallback(() => {
    if (previewFile) {
      setEditingContent(previewFile.content);
      setIsEditing(true);
      setSaveError(null);
    }
  }, [previewFile]);
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditingContent(null);
    setSaveError(null);
  }, []);
  const saveEdit = useCallback(async () => {
    if (!skill || !previewFile || editingContent === null) {
      return;
    }
    setSaveError(null);
    try {
      await uploadFile.mutateAsync({
        path: previewFile.path,
        content: editingContent,
      });
      setPreviewFile((prev) =>
        prev ? { ...prev, content: editingContent } : null,
      );
      setIsEditing(false);
      setEditingContent(null);
    } catch (err) {
      setSaveError(getApiErrorMessage(err, 'Failed to save file'));
    }
  }, [skill, previewFile, editingContent, uploadFile]);
  const handleDeleteFile = useCallback(
    async (filePath: string) => {
      if (!skill) {
        return;
      }
      setDeleteFileError(null);
      try {
        await deleteFile.mutateAsync(filePath);
        if (previewFile?.path === filePath) {
          setPreviewFile(null);
        }
        setConfirmDeleteFile(null);
      } catch (err) {
        setDeleteFileError(getApiErrorMessage(err, 'Failed to delete file'));
      }
    },
    [skill, deleteFile, previewFile],
  );
  return {
    // Drag and drop
    isDragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    dropError,
    clearDropError: () => setDropError(null),
    // Upload progress
    uploadProgress,
    // File preview
    previewFile,
    loadFileContent,
    closePreview: () => {
      setPreviewFile(null);
      setIsEditing(false);
      setEditingContent(null);
      setSaveError(null);
    },
    // Inline editing
    isEditing,
    editingContent,
    setEditingContent,
    startEditing,
    cancelEditing,
    saveEdit,
    saveError,
    isSaving: uploadFile.isPending,
    // Add file
    showAddFile,
    setShowAddFile,
    // Delete file
    confirmDeleteFile,
    setConfirmDeleteFile,
    handleDeleteFile,
    deleteFileError,
    clearDeleteFileError: () => setDeleteFileError(null),
    isDeleting: deleteFile.isPending,
  };
}
